import { useRef, useEffect, useState, SetStateAction } from "react";
import {
  draggable,
  dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import {
  attachClosestEdge,
  type Edge,
  extractClosestEdge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import { autoScrollForElements } from "@atlaskit/pragmatic-drag-and-drop-auto-scroll/element";

import {
  Box,
  IconButton,
  Stack,
  Typography,
  useTheme,
  alpha,
  Tooltip,
  Grid,
} from "@mui/material";
import { GridDeleteIcon } from "@mui/x-data-grid";
import { DragIndicator, Edit } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import NewAlertModal from "../../../componentes/newAlertModal";
import { borderLeft, borderRight } from "@mui/system";

type FilterState =
  | { type: "idle" }
  | { type: "is-dragging" }
  | { type: "is-dragging-over"; closestEdge: Edge | null };

const idle: FilterState = { type: "idle" };

export default function DraggableFilter({
  filter,
  index,
  onReorder,
  handleDeleteFilter,
  setIsFilterEditor,
  setPageId,
  boxRef,
  ...props
}: {
  filter: Filters;
  selectedPageId: string;
  index: number;
  onReorder: (from: number, to: number) => void;
  handleDeleteFilter: (value: string) => void;
  setIsFilterEditor: React.Dispatch<SetStateAction<boolean>>;
  setPageId: React.Dispatch<SetStateAction<string>>;
  boxRef: React.RefObject<HTMLDivElement>;
}) {
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [state, setState] = useState<FilterState>(idle);
  const draggableRef = useRef<HTMLDivElement>(null);
  const dropTargetRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const navigate = useNavigate();
  const styles = {
    boxContainerFilters: {
      p: 1,
      display: "flex",
      flexDirection: "row",
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.stroke}`,
      borderLeft: "none",
      borderRight: "none",
      width: "100%",
      // overflow: "auto",
    },
    stackIconButtons: {
      display: "flex",
      flexDirection: "row",
    },
    stackTypografy: {
      display: "flex",
      width: "100%",
      minWidth: 50,
    },
    label: {
      fontSize: "0.875rem",
      color: theme.palette.text.secondary,
    },
    value: {
      fontSize: "1rem",
      color: theme.palette.text.primary,
      textOverflow: "ellipsis",
      overflow: "hidden",
      whiteSpace: "nowrap",
    },
  };
  useEffect(() => {
    const element = draggableRef.current;
    if (!element) return;

    return draggable({
      element,
      getInitialData: () => ({ id: filter.variable, index }),
      onDragStart: () => {
        element.style.opacity = "0.3";
        element.style.transform = "scale(1.02)";
      },
      onDrop: () => {
        element.style.opacity = "1";
        element.style.transform = "scale(1)";
      },
    });
  }, [filter.variable, index]);

  useEffect(() => {
    if (!dropTargetRef?.current || !boxRef?.current) return;

    const cleanup = combine(
      dropTargetForElements({
        element: dropTargetRef.current,
        canDrop({ source }) {
          return source.data?.id !== filter.variable;
        },
        getData: ({ input }) => {
          const data = { id: filter.variable, index };

          return attachClosestEdge(data, {
            element: dropTargetRef.current!,
            input,
            allowedEdges: ["top", "bottom"],
          });
        },
        getIsSticky() {
          return true;
        },
        onDragEnter({ self }) {
          const closestEdge = extractClosestEdge(self.data);
          setState({ type: "is-dragging-over", closestEdge });
          if (dropTargetRef.current) {
            dropTargetRef.current.style.backgroundColor = alpha(
              theme.palette.primary.main,
              0.1
            );
            dropTargetRef.current.style.border = `1px dashed ${theme.palette.primary.main}`;
            dropTargetRef.current.style.padding = "8px";
            dropTargetRef.current.style.borderRadius = "0.3rem";
          }
        },
        onDrag({ self }) {
          const closestEdge = extractClosestEdge(self.data);
          setState((current) => {
            if (
              current.type === "is-dragging-over" &&
              current.closestEdge === closestEdge
            ) {
              return current;
            }
            return { type: "is-dragging-over", closestEdge };
          });
        },
        onDragLeave() {
          setState(idle);
          if (dropTargetRef.current) {
            dropTargetRef.current.style.backgroundColor = "";
            dropTargetRef.current.style.border = "none";
            dropTargetRef.current.style.padding = "0";
            dropTargetRef.current.style.borderRadius = "0";
          }
        },
        onDrop: ({ source }) => {
          setState(idle);
          if (source.data.id !== filter.variable) {
            onReorder(Number(source.data.index), index);
          }
          if (dropTargetRef.current) {
            dropTargetRef.current.style.backgroundColor = "";
            dropTargetRef.current.style.border = "none";
            dropTargetRef.current.style.padding = "0";
            dropTargetRef.current.style.borderRadius = "0";
          }
        },
      }),
      autoScrollForElements({
        element: boxRef.current,
      })
    );

    return () => {
      cleanup();
    };
  }, [dropTargetForElements, filter.variable, index, onReorder]);

  return (
    <>
      <Box ref={dropTargetRef}>
        <Box
          key={"box-drag"}
          ref={draggableRef}
          sx={{
            width: "100%",
            cursor: "grab",
            "&:active": { cursor: "grabbing" },
          }}
          {...props}
        >
          <Grid container columns={12}>
            <Box sx={styles.boxContainerFilters}>
              <Grid size={1}>
                <DragIndicator
                  fontSize="small"
                  sx={{ color: theme.palette.text.secondary }}
                />
              </Grid>
              <Grid size={2.75}>
                <Tooltip title={filter.label}>
                  <Typography sx={styles.value}>{filter.label}</Typography>
                </Tooltip>
              </Grid>
              <Grid size={2.75}>
                <Tooltip title={filter.column}>
                  <Typography sx={styles.value}>{filter.column}</Typography>
                </Tooltip>
              </Grid>
              <Grid size={2.75}>
                <Tooltip title={filter.variable}>
                  <Typography sx={styles.value}>{filter.variable}</Typography>
                </Tooltip>
              </Grid>
              <Grid size={2.75}>
                <Stack direction="row" spacing={1}>
                  <IconButton
                    sx={{ color: theme.palette.text.primary }}
                    disabled={!!!props.selectedPageId}
                    onClick={() => {
                      setIsFilterEditor(true);
                      setPageId(props.selectedPageId);
                      navigate(
                        `/configuracoes/filters/edit/${props.selectedPageId}/${filter.variable}`
                      );
                    }}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    sx={{ color: theme.palette.cromatic.red.main }}
                    onClick={() => setOpenDeleteModal(true)}
                  >
                    <GridDeleteIcon />
                  </IconButton>
                </Stack>
              </Grid>
            </Box>
          </Grid>
          {/* <Box
            key={filter.variable}
            sx={{
              ...styles.boxContainerFilters,
            }}
          >
            <Stack
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                width: "100%",
                gap: 3,
              }}
            >
              <Stack
                sx={{
                  ...styles.stackTypografy,
                  width: "fit-content",
                  minWidth: "fit-content",
                }}
              >
                <DragIndicator
                  fontSize="small"
                  sx={{ color: theme.palette.text.secondary }}
                />
              </Stack>
              <Stack sx={styles.stackTypografy}>
                <Tooltip title={filter.label}>
                  <Typography sx={styles.value}>{filter.label}</Typography>
                </Tooltip>
              </Stack>
              <Stack sx={styles.stackTypografy}>
                <Tooltip title={filter.column}>
                  <Typography sx={styles.value}>{filter.column}</Typography>
                </Tooltip>
              </Stack>
              <Stack sx={styles.stackTypografy}>
                <Tooltip title={filter.variable}>
                  <Typography sx={styles.value}>{filter.variable}</Typography>
                </Tooltip>
              </Stack>
              <Stack sx={styles.stackIconButtons}>
                <IconButton
                  sx={{ color: theme.palette.text.primary }}
                  disabled={!!!props.selectedPageId}
                  onClick={() => {
                    setIsFilterEditor(true);
                    setPageId(props.selectedPageId);
                    navigate(
                      `/configuracoes/filters/edit/${props.selectedPageId}/${filter.variable}`
                    );
                  }}
                >
                  <Edit />
                </IconButton>
                <IconButton
                  sx={{ color: theme.palette.cromatic.red.main }}
                  onClick={() => setOpenDeleteModal(true)}
                >
                  <GridDeleteIcon />
                </IconButton>
              </Stack>
            </Stack>
          </Box> */}
        </Box>
      </Box>
      <NewAlertModal
        openModal={openDeleteModal}
        setOpenModal={setOpenDeleteModal}
        buttonText="Excluir"
        buttonCancelText="Cancelar"
        confirmar={false}
        handleButton={() => {
          handleDeleteFilter(filter.variable);
        }}
        handleCancel={() => {
          setOpenDeleteModal(false);
        }}
        severity="error"
        singleButton={false}
        textContent={`Tem certeza que deseja excluir o filtro de ${filter.label}?`}
      />
    </>
  );
}
